import React, { useEffect, useState } from "react";
import {
  FaHeart,
  FaUsers,
  FaRupeeSign,
  FaHistory,
  FaStar
} from "react-icons/fa";
import { CiCalendarDate } from "react-icons/ci";
import { IoIosArrowDown } from "react-icons/io";
import { RxDotFilled } from "react-icons/rx";
import { PiSlidersHorizontalBold } from "react-icons/pi";
import { IoClose } from "react-icons/io5";
import { IoIosMusicalNotes } from "react-icons/io";

import "./FilterBar.css";

const iconMap = {
  PiSlidersHorizontalBold,
  FaHeart,
  FaUsers,
  FaRupeeSign,
  FaHistory,
  FaStar,
  IoIosArrowDown,
  RxDotFilled,
  IoIosMusicalNotes,
  CiCalendarDate
};

const FilterBar = ({ filterList, filtersAndSort, setFiltersAndSort, setShowFilter, filterWiseCount }) => {
  const [selectedPills, setSelectedPills] = useState([]);
  const [selectedDropdowns, setSelectedDropdowns] = useState({});
  const [selectionOrder, setSelectionOrder] = useState([]);

  // 🟣 Keep local state in sync when parent filtersAndSort changes (like URL reload)
  useEffect(() => {
    if (!filtersAndSort) return;

    const dropdownsChanged =
      JSON.stringify(filtersAndSort.selectedDropdowns) !==
      JSON.stringify(selectedDropdowns);
    const pillsChanged =
      JSON.stringify(filtersAndSort.selectedPills) !==
      JSON.stringify(selectedPills);

    if (dropdownsChanged) setSelectedDropdowns(filtersAndSort.selectedDropdowns || {});
    if (pillsChanged) setSelectedPills(filtersAndSort.selectedPills || []);
  }, [filtersAndSort]);



  // 🟣 Handle Pill click
  const handlePillClick = (label) => {
    setSelectedPills((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );

    setSelectionOrder((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label) // remove when deselected
        : [...prev, label] // push to end when selected
    );
  };

  const handleRemovePill = (label) => {
    setSelectedPills((prev) => prev.filter((item) => item !== label));
    setSelectionOrder((prev) => prev.filter((item) => item !== label));
  };

  // 🟣 Handle Dropdown change
  const handleDropdownChange = (label, value) => {
    setSelectedDropdowns((prev) => ({
      ...prev,
      [label]: value
    }));

    setSelectionOrder((prev) =>
      prev.includes(label) ? prev : [...prev, label]
    );
  };

  const handleRemoveDropdown = (label) => {
    setSelectedDropdowns((prev) => {
      const updated = { ...prev };
      delete updated[label];
      return updated;
    });

    setSelectionOrder((prev) => prev.filter((item) => item !== label));
  };

  // Update parent with filters
  useEffect(() => {
    // Only update parent if the data actually changed
    setFiltersAndSort((prev) => {
      const prevDropdowns = JSON.stringify(prev.selectedDropdowns || {});
      const currDropdowns = JSON.stringify(selectedDropdowns);

      const prevPills = JSON.stringify(prev.selectedPills || []);
      const currPills = JSON.stringify(selectedPills);

      if (prevDropdowns !== currDropdowns || prevPills !== currPills) {
        return { selectedPills, selectedDropdowns };
      }
      return prev; // No change → no re-render
    });
  }, [selectedPills, selectedDropdowns]);


  // 🟣 Sort filters for rendering
  const sortedFilters = [...filterList].sort((a, b) => {
    const aIndex = selectionOrder.indexOf(a.label);
    const bIndex = selectionOrder.indexOf(b.label);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex; // both selected → keep order
    if (aIndex !== -1) return -1; // a selected first
    if (bIndex !== -1) return 1; // b selected first
    return 0; // neither selected → keep original order
  });

  return (
    <div className="filter-container">
      {sortedFilters.map((filter, index) => {
        const Icon = iconMap[filter.icon];

        // 1. Dropdown
        if (filter.type === "dropdown") {
          const isSelected = selectedDropdowns[filter.label];
          return (
            <div key={index} className="filter-dropdown-wrapper">
              <div
                className={`filter-dropdown-display ${isSelected ? "active" : ""
                  }`}
              >
                <select
                  className="filter-dropdown"
                  value={selectedDropdowns[filter.label] || ""}
                  onChange={(e) =>
                    handleDropdownChange(filter.label, e.target.value)
                  }
                >
                  <option
                    className="dropdown-placeholder"
                    value=""
                    disabled
                    hidden
                  >
                    {filter.label}
                  </option>
                  {filter.options &&
                    filter.options.map((opt, i) => (
                      <option className="dropdown-placeholder" key={i} value={opt}>
                        {isSelected && filterWiseCount?.[opt] ? `(${filterWiseCount[opt]})` : ""}{" "}
                        {opt}
                      </option>
                    ))}
                </select>
                {isSelected && (
                  <span
                    className="clear-icon"
                    onClick={() => handleRemoveDropdown(filter.label)}
                  >
                    <IoClose />
                  </span>
                )}
              </div>
            </div>
          );
        }

        // 2. Pill
        if (filter.type === "filter") {
          const isActive = selectedPills.includes(filter.label);
          return (
            <button
              key={index}
              className={`filter-pill ${isActive ? "active" : ""}`}
              onClick={() => handlePillClick(filter.label)}
            >
              {Icon && <Icon style={{ marginRight: 6 }} />}
              <span className="filter-count-pill">
                 {isActive && filterWiseCount?.[filter.label] ? `(${filterWiseCount[filter.label]})` : ""}
              </span>
              {filter.label}
              {isActive && (
                <span
                  className="clear-icon"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent re-triggering selection
                    handleRemovePill(filter.label);
                  }}
                >
                  <IoClose />
                </span>
              )}
            </button>
          );
        }

        // 3. Default Button
        return (
          <button
            key={index}
            className="filter-btn-bar"
            onClick={() => {
              setShowFilter(true);
            }}
          >
            {Icon && <Icon style={{ marginRight: 6 }} />}
            {filter.label}
          </button>
        );
      })}
    </div>
  );
};

export default FilterBar;
